import { useContext, useState, useEffect } from "react";
import {
  VYLABS_BASE_URL,
  LIST_MEMEBERS_OF_TENANT,
} from "../../helper/url_helper.js";
import { get } from "../../helper/Api_helper.js";
import DynamicAutocomplete from "../AutoSuggestion/DynamicAutocomplete.js";
import { DynamicContext } from "../../Context/DynamicContext.js";
import { TENANT_CORE_API_KEY } from "../../Tenant-Config/TenantConfig.js";

const DynamicListUsers = ({
  label,
  onChange,
  value,
  placeholder,
  onBlur,
  error,
  errorMessage,
  showMe = false,
  usersSx = {},
  usersComponentProps,
  popupIcon,
}) => {
  const { userDetails } = useContext(DynamicContext);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      return;
    }

    if (typeof value === "object") {
      setSelectedUser(value);
      return;
    }

    const matchingUser = users.find(
      (user) => String(user.value) === String(value),
    );

    if (matchingUser) {
      setSelectedUser(matchingUser);
    } else if (showMe && String(value) === String(userDetails?.id)) {
      setSelectedUser({ value: userDetails.id, label: "Me" });
    }
  }, [value, users, showMe, userDetails?.id]);

  const meOption =
    showMe && userDetails?.id
      ? { value: userDetails.id, label: "Me" }
      : null;

  const displayUsers = (() => {
    const options = [...users];

    if (meOption && !options.some((u) => String(u.value) === String(meOption.value))) {
      options.unshift(meOption);
    }

    if (selectedUser?.value && !options.some((u) => String(u.value) === String(selectedUser.value))) {
      options.unshift(selectedUser);
    }

    return options;
  })();

  const formatUsers = (response) => {
    const users = Array.isArray(response?.users)
      ? response.users
          .map((item) => item?.user)
          .filter(Boolean)
      : [];
  
    return users.map((user) => ({
      ...user,
      value: user?.id,
      label:
        [user?.first_name, user?.last_name].filter(Boolean).join(" ") 
    }));
  };

  const listUsers = async () => {
    setLoading(true);

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails?.tenant_id}&offset=0&limit=30`,
        {
          headers: {
            "X-API-Key": TENANT_CORE_API_KEY,
          },
        }
      );

      setUsers((currentUsers) => {
        const formattedUsers = formatUsers(response);
        const selected = selectedUser;
        if (selected?.value && !formattedUsers.some((u) => String(u.value) === String(selected.value))) {
          return [selected, ...formattedUsers];
        }
        return formattedUsers;
      });
    } catch (error) {
      console.error("error in listUsers", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = async (filter) => {
    setLoading(true);

    try {
      const response = await get(
        VYLABS_BASE_URL,
        `${LIST_MEMEBERS_OF_TENANT}?tenant_id=${userDetails?.tenant_id}&filter=${encodeURIComponent(
          filter
        )}&offset=0&limit=30`,
        {
          headers: {
            "X-API-Key": TENANT_CORE_API_KEY,
          },
        }
      );

      setUsers((currentUsers) => {
        const formattedUsers = formatUsers(response);
        const selected = selectedUser;
        if (selected?.value && !formattedUsers.some((u) => String(u.value) === String(selected.value))) {
          return [selected, ...formattedUsers];
        }
        return formattedUsers;
      });
    } catch (error) {
      console.error("error in filterUsers", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    // Initial click/open: show the normal list.
    if (users.length === 0) {
      listUsers();
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === "input") {
      // Search on every character:
      // "g" -> filter=g
      // "ga" -> filter=ga
      // "gar" -> filter=gar
      if (newInputValue) {
        filterUsers(newInputValue);
      } else {
        listUsers();
      }
    }

    // Clicking the clear button gives reason === "clear".
    if (reason === "clear") {
      listUsers();
    }
  };

  return (
    <div>
      <DynamicAutocomplete
        label={label}
        value={selectedUser}
        options={displayUsers}
        placeholder={placeholder}
        popupIcon={popupIcon}
        onChange={(selected) => {
          setSelectedUser(selected || null);
          onChange?.(selected);
        }}
        onBlur={onBlur}
        error={error}
        errorMessage={errorMessage}
        loading={loading}
        onOpen={handleOpen}
        onInputChange={handleInputChange}
        componentsProps={usersComponentProps}
        sx={{
          ".MuiOutlinedInput-root": {
            borderRadius: "12px",
            height: "40px",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid var(--border__color) !important",
              borderRadius: "12px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#6860ee",
              borderRadius: "12px",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#7a70f2",
              borderRadius: "12px",
            },
          },
          ".MuiInputBase-input": {
            color: "var(--header__color)",
            padding: "8px 12px !important",
            height: "40px",
            fontFamily: "Satoshi !important",
          },
          ...usersSx,
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "var(--body__background)",
              color: "var(--header__color)",
              borderRadius: "12px",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid var(--border__color)",
            },
          },
          listbox: {
            sx: {
              "& li": {
                fontSize: "14px",
                fontWeight: 500,
                padding: "8px 16px",
                color: "var(--header__color)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              },
            },
          },
        }}
      />
    </div>
  );
};

export default DynamicListUsers;